/**
 * Per-chat event-cursor utilities + persisted map — PR-A1 of the
 * sync-event-cursor-deadlock plan (flag `syncEventDrainV2`).
 *
 * Cursor VALUES are one of two formats:
 *  - ISO timestamps (`Date.toISOString()`) — drain-advance values and seeds.
 *  - BE Redis-stream ids `${ms}-${seq}` — mid-pagination per-chat checkpoints
 *    returned by `/sync/events` (the BE attaches the max stream id of the
 *    RETURNED events as the resumable per-chat cursor).
 *
 * ALL comparisons are numeric on `(ms, seq)`, mirroring the BE's
 * `parseCursor` (ow2-chat-be `eventsHandler.ts`): a stream id preserves its
 * seq; an ISO value parses as `{ ms: Date.parse(value), seq: 0 }` — the
 * lower-bound/`since` semantics the BE applies to ISO cursors. Lexical string
 * comparison is NEVER safe here: stream seqs have variable digit width, so
 * `"...-10" < "...-2"` lexically (Bug #2 — the silent event-skip class).
 */

import { db } from "@/indexedDB/db";

/**
 * Meta store key for the per-chat event-cursor map
 * (`Record<chatId, cursor>`). Meta-keyed JSON map by design (pattern:
 * `META_FAILED_MESSAGE_CHATS`) — ~10-100KB at 124-1000 chats, read whole every
 * run, checkpoint = one `meta.put`. No Dexie store / version bump.
 */
export const META_EVENT_CURSORS = "eventCursorsByChat" as const;

const STREAM_ID_RE = /^(\d+)-(\d+)$/;

export interface ParsedEventCursor {
  ms: number;
  seq: number;
}

/**
 * Parse a cursor that may be either a stream id (`${ms}-${seq}`) or an ISO
 * date string, into a numerically comparable `(ms, seq)` position. An
 * unparseable value degrades to the epoch (ms 0) — always below the TTL
 * floor, so the read-side clamp heals it to the floor instead of trusting
 * garbage.
 */
export function parseEventCursor(value: string): ParsedEventCursor {
  const match = STREAM_ID_RE.exec(value);
  if (match) {
    return { ms: Number(match[1]), seq: Number(match[2]) };
  }
  const ms = new Date(value).getTime();
  return { ms: Number.isNaN(ms) ? 0 : ms, seq: 0 };
}

/**
 * True when the value is a BE Redis-stream id (`${ms}-${seq}`), i.e. a
 * mid-pagination per-chat CHECKPOINT rather than an ISO drain-advance/seed.
 * A persisted stream-format cursor means the previous run was interrupted
 * before that chat fully drained — the signal the Telemetry PR counts for the
 * `checkpoint_resume` (resumed-interruption) and `laggard_count` metrics.
 */
export function isStreamEventCursor(value: string): boolean {
  return STREAM_ID_RE.test(value);
}

/** Age buckets for a per-chat cursor's position (NO raw timestamps ever). */
export type EventCursorAgeBucket =
  | "<1h"
  | "1-24h"
  | "1-3d"
  | "3-7d"
  | "7-14d"
  | ">=14d";

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

/**
 * Bucket a cursor AGE (ms elapsed since the cursor's position) into a coarse
 * band. Lower-inclusive boundaries (`>=`). A negative age (future clock skew)
 * or NaN clamps into the lowest bucket — telemetry must never leak a raw age,
 * and a skewed cursor is not a "pinned" signal. NO PII: the bucket label is
 * the only thing emitted, never the underlying age or timestamp.
 */
export function bucketEventCursorAge(ageMs: number): EventCursorAgeBucket {
  if (!Number.isFinite(ageMs) || ageMs < HOUR_MS) {
    return "<1h";
  }
  if (ageMs < DAY_MS) {
    return "1-24h";
  }
  if (ageMs < 3 * DAY_MS) {
    return "1-3d";
  }
  if (ageMs < 7 * DAY_MS) {
    return "3-7d";
  }
  if (ageMs < 14 * DAY_MS) {
    return "7-14d";
  }
  return ">=14d";
}

/** Numeric `(ms, seq)` comparator — negative / 0 / positive like Array.sort. */
export function compareEventCursors(a: string, b: string): number {
  const pa = parseEventCursor(a);
  const pb = parseEventCursor(b);
  if (pa.ms !== pb.ms) {
    return pa.ms < pb.ms ? -1 : 1;
  }
  if (pa.seq !== pb.seq) {
    return pa.seq < pb.seq ? -1 : 1;
  }
  return 0;
}

/**
 * ISO normalization for the LEGACY `META_EVENT_CURSOR` key: a stream id
 * floors to its millisecond as ISO; ISO values pass through unchanged.
 * Rollback builds' `clampReplaySince` compares that key LEXICALLY as
 * fixed-width ISO — a raw stream id written there would fail its window check
 * and silently degrade every rollback run to a full-TTL replay.
 */
export function isoFloorOfEventCursor(value: string): string {
  const match = STREAM_ID_RE.exec(value);
  if (!match) {
    return value;
  }
  return new Date(Number(match[1])).toISOString();
}

export type EventCursorClampReason =
  | "missing"
  | "below-floor"
  | "future"
  | null;

/**
 * Read-side clamp for a chat's effective `since` (the v2 equivalent of
 * `clampReplaySince`, which the delta path never had): a missing value or one
 * below the TTL floor becomes the floor; a FUTURE value (clock skew / corrupt
 * meta — strictly later millisecond than `nowIso`) is rejected to the floor so
 * a skewed cursor can't skip the retained window. In-window values pass
 * through untouched.
 */
export function clampEventCursorToWindow(
  value: string | undefined,
  floorIso: string,
  nowIso: string,
): { value: string; reason: EventCursorClampReason } {
  if (value === undefined) {
    return { value: floorIso, reason: "missing" };
  }
  if (compareEventCursors(value, floorIso) < 0) {
    return { value: floorIso, reason: "below-floor" };
  }
  // ms-strict future check: a same-millisecond stream seq is NOT "future".
  if (parseEventCursor(value).ms > parseEventCursor(nowIso).ms) {
    return { value: floorIso, reason: "future" };
  }
  return { value, reason: null };
}

/**
 * The persisted per-chat event-cursor map.
 *
 * INVARIANTS (review-blocking if violated — plan R5):
 *  - `advance` is the single MONOTONIC write site for normal progress (the
 *    only other writer is `repair`, the documented read-clamp heal for
 *    entries already outside the valid window — see its doc):
 *    `max(existing, candidate)` under the numeric comparator. BE page cursors
 *    can sit BELOW a chat's persisted position when it is co-batched with a
 *    laggard (batch `since` = the laggard's older position → the BE re-returns
 *    older events), and persisting that regression would reintroduce the
 *    replay storm.
 *  - `checkpoint` (the only `meta.put`) must be called AFTER the page's
 *    per-chat apply transactions committed — a crash between apply and
 *    checkpoint replays idempotent events, never loses them — and always runs
 *    inside the sync lock (`runSync` executes under `withSyncLock`).
 */
export class EventCursorStore {
  private dirty = false;

  private constructor(private readonly map: Record<string, string>) {}

  static async load(): Promise<EventCursorStore> {
    const entry = await db.meta.get(META_EVENT_CURSORS);
    const raw = entry?.value;
    const map: Record<string, string> = {};
    if (raw && typeof raw === "object" && !Array.isArray(raw)) {
      for (const [chatId, cursor] of Object.entries(
        raw as Record<string, unknown>,
      )) {
        // Tolerate corrupt entries (only strings are cursors) — a bad entry is
        // dropped and the chat re-seeds from the legacy/TTL fallback chain.
        if (typeof cursor === "string") {
          map[chatId] = cursor;
        }
      }
    }
    return new EventCursorStore(map);
  }

  get(chatId: string): string | undefined {
    return this.map[chatId];
  }

  /**
   * THE single guarded write site — monotonic per-chat advance. Returns true
   * only when the candidate actually moved the chat's position forward.
   */
  advance(chatId: string, candidate: string): boolean {
    const existing = this.map[chatId];
    if (
      existing !== undefined &&
      compareEventCursors(candidate, existing) <= 0
    ) {
      return false;
    }
    this.map[chatId] = candidate;
    this.dirty = true;
    return true;
  }

  /**
   * Read-clamp healing — the ONLY write besides `advance`, and the only one
   * allowed to LOWER a value. Used exclusively when the read-side clamp finds
   * a persisted entry OUTSIDE `[floor, now]` (future clock-skew / corrupt
   * meta, or below the TTL floor). Without healing, a FUTURE entry would
   * defeat the monotonic `advance` forever — every real candidate (BE page
   * cursors, `until`) compares below it — so the chat would replay its whole
   * window every run and leak the bogus value into the derived legacy
   * cursor. Persisted at the next checkpoint. (Impl-review round 1 fix.)
   */
  repair(chatId: string, value: string): void {
    if (this.map[chatId] === value) {
      return;
    }
    this.map[chatId] = value;
    this.dirty = true;
  }

  /**
   * Persist the map (one `meta.put`). Call only AFTER the page's apply
   * transactions committed — see the class invariants. No-op when clean.
   */
  async checkpoint(): Promise<void> {
    if (!this.dirty) {
      return;
    }
    this.dirty = false;
    await db.meta.put({ key: META_EVENT_CURSORS, value: { ...this.map } });
  }
}
